import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navigation from "../features/landing/shared/Navigation";

const schemes = [
	{
		name: "PPF",
		description:
			"Public Provident Fund (PPF) is a long-term government-backed savings scheme with tax benefits and guaranteed returns.",
		link: "https://articles.buildmywealth.net/government-schem/ppf-calculator",
	},
	{
		name: "NPS",
		description:
			"National Pension System (NPS) is a voluntary retirement savings scheme to encourage systematic savings for retirement.",
		link: "https://articles.buildmywealth.net/government-schem/ppf-calculator",
	},
	{
		name: "Sukanya Samriddhi",
		description:
			"Sukanya Samriddhi Yojana is a government savings scheme for the girl child, offering attractive interest rates and tax benefits.",
		link: "https://articles.buildmywealth.net/government-schem/ppf-calculator",
	},
	{
		name: "SCSS",
		description:
			"Senior Citizens Savings Scheme (SCSS) is a government-backed savings instrument for individuals above 60 years, offering regular income and tax benefits.",
		link: "https://articles.buildmywealth.net/government-schem/ppf-calculator",
	},
	{
		name: "APY",
		description:
			"Atal Pension Yojana (APY) is a pension scheme focused on unorganized sector workers, providing a fixed minimum pension after retirement.",
		link: "https://articles.buildmywealth.net/government-schem/ppf-calculator",
	},
];

const services = [
	{
		name: "SIP Planning",
		url: "https://articles.buildmywealth.net/sip-planning",
	},
	{
		name: "Goal-based Investment",
		url: "https://articles.buildmywealth.net/goal-based-investment",
	},
	{
		name: "Retirement Planning",
		url: "https://articles.buildmywealth.net/retirement-planning",
	},
	{
		name: "Child Education",
		url: "https://articles.buildmywealth.net/child-education",
	},
	{
		name: "Portfolio Review",
		url: "https://articles.buildmywealth.net/portfolio-review",
	},
	{
		name: "Tax Saving Funds",
		url: "https://articles.buildmywealth.net/tax-saving-funds",
	},
];

const GovernmentSchemesPage = () => {
	const { schemeName } = useParams();
	const navigate = useNavigate();
	const [selected, setSelected] = React.useState(0);
	const [mobileOpen, setMobileOpen] = React.useState(false);

	// Find scheme by URL param if present
	React.useEffect(() => {
		if (schemeName) {
			const idx = schemes.findIndex(
				(s) =>
					s.name.toLowerCase().replace(/\s+/g, "-") === schemeName.toLowerCase()
			);
			if (idx !== -1) setSelected(idx);
			else setSelected(0); // fallback to first
		}
	}, [schemeName]);

	// Update URL when selected changes (for mobile/desktop nav)
	React.useEffect(() => {
		const slug = schemes[selected].name.toLowerCase().replace(/\s+/g, "-");
		if (schemeName !== slug) {
			navigate(`/government-schemes/${slug}`, { replace: true });
		}
		// eslint-disable-next-line
	}, [selected]);

	return (
		<div className="min-h-screen flex flex-col bg-gray-50">
			<Navigation />
			<main className="flex flex-1 flex-col md:flex-row py-12 px-4 md:px-8 lg:px-16 xl:px-24">
				{/* Side Panel - Mobile (now on top) */}
				<div className="md:hidden mb-8 py-6">
					<button
						className="mb-4 px-4 py-4 bg-indigo-600 text-white rounded-lg font-semibold shadow focus:outline-none"
						onClick={() => setMobileOpen((open) => !open)}
						aria-label="Show government schemes menu"
					>
						{mobileOpen ? "Hide Schemes" : "Show Schemes"}
					</button>
					{mobileOpen && (
						<nav className="mb-4 bg-white border rounded-lg shadow p-2 space-y-1">
							{schemes.map((scheme, idx) => (
								<button
									key={scheme.name}
									className={`block w-full text-left px-4 py-2 rounded-lg font-medium transition-colors ${
										selected === idx
											? "bg-indigo-100 text-indigo-700"
											: "hover:bg-indigo-50 text-gray-700"
									}`}
									onClick={() => {
										setSelected(idx);
										setMobileOpen(false);
									}}
								>
									{scheme.name}
								</button>
							))}
						</nav>
					)}
				</div>
				{/* Side Panel - Desktop */}
				<aside className="w-64 bg-white border-r px-4 py-12 hidden md:block">
					<h2 className="text-xl font-bold mb-6 text-indigo-700">
						Government Schemes
					</h2>
					<nav className="space-y-2">
						{schemes.map((scheme, idx) => (
							<button
								key={scheme.name}
								className={`block w-full text-left px-4 py-2 rounded-lg font-medium transition-colors ${
									selected === idx
										? "bg-indigo-100 text-indigo-700"
										: "hover:bg-indigo-50 text-gray-700"
								}`}
								onClick={() => setSelected(idx)}
							>
								{scheme.name}
							</button>
						))}
					</nav>
				</aside>
				{/* Main Content */}
				<section className="flex-1 max-w-3xl mx-auto px-4 py-12 flex flex-col justify-between">
					<div>
						<h1 className="text-3xl md:text-4xl font-bold mb-4 text-indigo-700">
							{schemes[selected].name}
						</h1>
						<p className="text-lg text-gray-700 mb-6">
							{schemes[selected].description}
						</p>
						<a
							href={schemes[selected].link}
							target="_blank"
							rel="noopener noreferrer"
							className="text-indigo-600 hover:underline"
						>
							Read more about {schemes[selected].name}
						</a>
						<div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-900 mb-6 mt-6">
							<strong>Build authority, help with informed decisions:</strong> Learn
							about key government-backed investment and savings schemes to make the
							best choices for your financial future.
						</div>
					</div>
					<div className="mt-8">
						<h2 className="text-xl font-semibold mb-2 text-indigo-700">
							Related Articles on Our Knowledge Base
						</h2>
						<ul className="list-disc pl-6 space-y-1">
							{services.map((service) => (
								<li key={service.url}>
									<a
										href={service.url}
										target="_blank"
										rel="noopener noreferrer"
										className="text-indigo-600 hover:underline"
									>
										{service.name}
									</a>
								</li>
							))}
						</ul>
					</div>
				</section>
			</main>
			{/* Floating Sticky Bottom Button (global, right side, non-blocking) */}
			<a
				href="https://wa.me/919616622620?text=Hi%20Mukesh%2C%20I%20have%20a%20question%20about%20government%20schemes."
				target="_blank"
				rel="noopener noreferrer"
				className="fixed bottom-4 right-4 z-50 px-6 py-3 bg-green-600 text-white font-semibold rounded-full shadow-lg hover:bg-green-700 transition-all flex items-center gap-2 md:right-8"
				style={{ minWidth: 180, pointerEvents: "auto" }}
			>
				<svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
					<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
				</svg>
				Talk to Mukesh
			</a>
		</div>
	);
};

export default GovernmentSchemesPage;
